import dayjs from 'dayjs';

export const ISO_DATE = 'YYYY-MM-DD';

/** What production is commonly measured in, offered in the unit pickers; any other unit can be typed. */
export const OUTPUT_UNIT_SUGGESTIONS = ['tons', 'tonnes', 'units', 'cases', 'lb', 'kg', 'gallons'];

export const DEFAULT_OUTPUT_UNIT = 'tons';

/** How far a value may sit from the median before the grid flags it. */
export const ANOMALY_TOLERANCE = 0.35;

/** Parses a typed quantity, tolerating the thousands separators people paste from Excel. */
export function parseQuantity (value) {
    if (value === null || value === undefined) return null;

    const text = String(value).replace(/[\s,]/g, '');
    if (text === '') return null;

    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : NaN;
}

export function formatQuantity (value, maximumFractionDigits = 3) {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    return Number(value).toLocaleString(undefined, { maximumFractionDigits });
}

/** Days in an inclusive period, or null when either end is missing. */
export function periodDays (start, end) {
    if (!start || !end) return null;
    return dayjs(end).diff(dayjs(start), 'day') + 1;
}

/** The value of the period picker that means the trailing twelve months rather than one year. */
export const LAST_TWELVE_MONTHS = 'last12';

/**
 * The inclusive ISO date range the grids open on: a calendar year, or for
 * {@link LAST_TWELVE_MONTHS} the current month and the eleven before it, so this month's bill
 * has somewhere to go as soon as it arrives.
 */
export function entryWindow (choice, today = dayjs()) {
    if (choice === LAST_TWELVE_MONTHS) {
        const end = dayjs(today).endOf('month');
        return {
            from: end.subtract(11, 'month').startOf('month').format(ISO_DATE),
            to: end.format(ISO_DATE),
        };
    }

    return { from: `${choice}-01-01`, to: `${choice}-12-31` };
}

/** The calendar months touching an inclusive ISO range, as inclusive ISO period pairs. */
export function monthsInRange (from, to) {
    const months = [];
    for (let first = dayjs(from).startOf('month'); !first.isAfter(dayjs(to)); first = first.add(1, 'month')) {
        months.push({
            label: first.format('MMMM YYYY'),
            periodStart: first.format(ISO_DATE),
            periodEnd: first.endOf('month').format(ISO_DATE),
        });
    }
    return months;
}

/** Days a new period covers when there is nothing to judge a typical period by. */
export const DEFAULT_PERIOD_DAYS = 30;

/** The median length of the rows' periods in days, so a new row matches the meter's billing cycle. */
export function typicalPeriodDays (rows, fallback = DEFAULT_PERIOD_DAYS) {
    const lengths = rows
        .map(row => periodDays(row.periodStart, row.periodEnd))
        .filter(days => days && days > 0)
        .sort((a, b) => a - b);

    if (lengths.length === 0) return fallback;

    const middle = Math.floor(lengths.length / 2);
    return lengths.length % 2 === 0 ? Math.round((lengths[middle - 1] + lengths[middle]) / 2) : lengths[middle];
}

/**
 * The period that would sit directly before the first row or after the last, as ISO dates.
 *
 * Bills are read to read, so a new period starts the day after the last one ended and runs for the
 * meter's typical cycle. With `monthly`, a row that falls on month boundaries is followed or
 * preceded by the whole adjacent calendar month instead, which is how production is reported.
 *
 * @param rows rows with ISO periodStart/periodEnd; rows missing either are ignored
 * @param direction 'before' or 'after'
 * @param fallbackStart ISO date to start from when there are no rows
 */
export function adjacentPeriod (rows, direction, { fallbackStart, monthly = false } = {}) {
    const dated = rows
        .filter(row => row.periodStart && row.periodEnd)
        .slice()
        .sort((a, b) => a.periodStart.localeCompare(b.periodStart));

    const length = typicalPeriodDays(dated);

    if (dated.length === 0) {
        const start = dayjs(fallbackStart);
        const end = monthly ? start.endOf('month') : start.add(length - 1, 'day');
        return { periodStart: start.format(ISO_DATE), periodEnd: end.format(ISO_DATE) };
    }

    if (direction === 'before') {
        const end = dayjs(dated[0].periodStart).subtract(1, 'day');
        const start = monthly && end.date() === end.daysInMonth()
            ? end.startOf('month')
            : end.subtract(length - 1, 'day');
        return { periodStart: start.format(ISO_DATE), periodEnd: end.format(ISO_DATE) };
    }

    const latestEnd = dated.reduce((latest, row) => (row.periodEnd > latest ? row.periodEnd : latest), dated[0].periodEnd);
    const start = dayjs(latestEnd).add(1, 'day');
    const end = monthly && start.date() === 1 ? start.endOf('month') : start.add(length - 1, 'day');
    return { periodStart: start.format(ISO_DATE), periodEnd: end.format(ISO_DATE) };
}

/** The range widened, where needed, to take in an inclusive ISO period. */
export function widenRange (range, period) {
    return {
        from: period.periodStart < range.from ? period.periodStart : range.from,
        to: period.periodEnd > range.to ? period.periodEnd : range.to,
    };
}

/**
 * Median of the per-day rates, used as the anomaly baseline. The median rather than the mean so a
 * single spike — the leak or the misplaced decimal you are looking for — does not hide itself.
 */
export function medianRate (rows) {
    const rates = rows
        .map(row => {
            const days = periodDays(row.periodStart, row.periodEnd);
            const quantity = parseQuantity(row.consumption ?? row.quantity);
            return days && days > 0 && quantity !== null && !Number.isNaN(quantity) ? quantity / days : null;
        })
        .filter(rate => rate !== null)
        .sort((a, b) => a - b);

    if (rates.length < 3) return null;

    const middle = Math.floor(rates.length / 2);
    return rates.length % 2 === 0 ? (rates[middle - 1] + rates[middle]) / 2 : rates[middle];
}

/** How far this row's per-day rate sits from the median, as a fraction, or null when unknowable. */
export function rateDeviation (row, median, field) {
    if (!median) return null;

    const days = periodDays(row.periodStart, row.periodEnd);
    const quantity = parseQuantity(row[field]);
    if (!days || days <= 0 || quantity === null || Number.isNaN(quantity)) return null;

    return (quantity / days - median) / median;
}

/**
 * Problems with the periods themselves, keyed by row key: overlaps, and the gap before a row.
 * The server enforces overlaps too; this is so the user sees it before they save.
 */
export function periodIssues (rows) {
    const issues = {};
    const ordered = rows
        .filter(row => row.periodStart && row.periodEnd)
        .slice()
        .sort((a, b) => a.periodStart.localeCompare(b.periodStart));

    ordered.forEach((row, index) => {
        if (dayjs(row.periodEnd).isBefore(dayjs(row.periodStart))) {
            issues[row.key] = { type: 'outOfOrder' };
            return;
        }

        if (index === 0) return;

        const previous = ordered[index - 1];
        if (row.periodStart <= previous.periodEnd) {
            issues[row.key] = { type: 'overlap' };
            return;
        }

        const gap = dayjs(row.periodStart).diff(dayjs(previous.periodEnd), 'day') - 1;
        if (gap > 0) {
            issues[row.key] = { type: 'gap', days: gap };
        }
    });

    return issues;
}

/** True when anything on the row differs from what was last saved. */
export function isRowDirty (row) {
    if (!row.saved) return true;
    return Object.keys(row.saved).some(field => (row[field] ?? '') !== (row.saved[field] ?? ''));
}

/** A stable key for a row that may not have a server id yet. */
let nextKey = 1;
export function newRowKey () {
    nextKey += 1;
    return `new-${nextKey}`;
}
